<template>
  <div>
    <div class="tab-body__grid">
      <ContactDialog
        v-if="modalVisible"
        @contactUpdated="modifyContact"
        @closeModal="closeModal"
        :currentContact="currentContact"
        :isByName="false"
      />
    </div>
    <div class="tab-body__grid">
      <ConfirmationModal
        v-if="confirmationModalVisible"
        @closeModal="closeConfirmationModal"
        @confirm="handleConfirm"
        :oldWebContactEmail="oldWebContactEmail"
        :newWebContactEmail="newWebContactEmail"
      />
    </div>
    <div class="tab-body__name">
      {{ facilityName }}
    </div>

    <div v-if="error" class="error">{{ error }}</div>
    <div v-else-if="!data" class="loading">Loading...</div>
    <div v-else-if="!data.success" class="error">{{ (data as ErrorResponse).error }}</div>
    <Grid
      v-else
      :style="{ height: 'auto' }"
      :data-items="data.response.ListItems"
      :columns="columns"
      resizable
      scrollable="true"
    >
      <template v-slot:EditRow="{ props: { dataItem } }">
        <td class="text-center k-table-td edit-icon">
          <font-awesome-icon icon="edit" @click="showModal(true, dataItem)" />
        </td>
      </template>
      <template v-slot:NumberOfFacilities="{ props: { dataItem, field } }">
        <td class="text-center k-table-td">
          {{ dataItem[field] }}
        </td>
      </template>
      <template v-slot:radioPrimaryCT="{ props: { dataItem, field } }">
        <td class="text-center k-table-td">
          <input
            type="radio"
            :disabled="readOnlyUser"
            :name="`${field}_${id}`"
            :checked="dataItem[field]"
            @change="updateField(dataItem.id, field, true)"
          />
        </td>
      </template>
      <template v-slot:radioBillingCT="{ props: { dataItem, field } }">
        <td class="text-center k-table-td">
          <input
            type="radio"
            :disabled="readOnlyUser"
            :name="`${field}_${id}`"
            :checked="dataItem[field]"
            @change="updateField(dataItem.id, field, true)"
          />
        </td>
      </template>
      <template v-slot:radioCopackerCT="{ props: { dataItem, field } }">
        <td class="text-center k-table-td">
          <input
            type="radio"
            :disabled="readOnlyUser"
            :name="`${field}_${id}`"
            :checked="dataItem[field]"
            @change="updateField(dataItem.id, field, true)"
          />
        </td>
      </template>
      <template v-slot:radioWebCT="{ props: { dataItem, field } }">
        <td class="text-center k-table-td">
          <input
            type="radio"
            :disabled="readOnlyUser"
            :name="`${field}_${id}`"
            :checked="dataItem[field]"
            @change="updateField(dataItem.id, field, true)"
          />
        </td>
      </template>
      <template v-slot:checkboxEir="{ props: { dataItem, field } }">
        <td class="text-center k-table-td">
          <input
            type="checkbox"
            :checked="dataItem[field]"
            @change="
              updateField(dataItem.id, field, handleCheckboxInputChange($event))
            "
          />
        </td>
      </template>
      <template v-slot:checkboxScheduleA="{ props: { dataItem, field } }">
        <td class="text-center k-table-td">
          <input
            type="checkbox"
            :checked="dataItem[field]"
            @change="
              updateField(dataItem.id, field, handleCheckboxInputChange($event))
            "
          />
        </td>
      </template>
      <template v-slot:checkboxScheduleB="{ props: { dataItem, field } }">
        <td class="text-center k-table-td">
          <input
            type="checkbox"
            :checked="dataItem[field]"
            @change="
              updateField(dataItem.id, field, handleCheckboxInputChange($event))
            "
          />
        </td>
      </template>
      <template v-slot:emailCell="{ props: { dataItem, field } }">
        <td class="k-table-td">
          <a :href="dataItem[field]">{{
            dataItem[field] ?? dataItem.Contact?.Email ?? ""
          }}</a>
        </td>
      </template>
      <template v-slot:deliveryMethod="{ props: { dataItem, field } }">
        <td class="k-table-td">
          <select
            :value="dataItem[field]"
            :disabled="readOnlyUser"
            @change="
              updateField(
                dataItem.id,
                field,
                handleDropdownListInputChange($event)
              )
            "
          >
            <option
              v-for="{ label, value } of deliveryMethods"
              :key="value"
              :value="value"
              :disabled="
                (dataItem['primaryCT'] ||
                  dataItem['billingCT'] ||
                  dataItem['copackerCT']) &&
                value == 3
              "
            >
              {{ label }}
            </option>
          </select>
        </td>
      </template>
      <template v-slot:DeleteRow="{ props: { dataItem } }">
        <td
          v-if="
            !dataItem['primaryCT'] &&
            !dataItem['billingCT'] &&
            !dataItem['webCT'] &&
            !dataItem['copackerCT']
          "
          class="text-center k-table-td"
        >
          <font-awesome-icon
            icon="times"
            class="del-icon"
            @click="removeRow(dataItem.id)"
          />
        </td>
        <td v-else class="text-center k-table-td">
          <div></div>
        </td>
      </template>
    </Grid>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onUpdated, computed } from "vue";
import type { GridColumnProps } from "@progress/kendo-vue-grid";
import { Grid } from "@progress/kendo-vue-grid";
import useSWRV from "swrv";
import { api } from "@/api";
import {
  type GetCustomerContactsRequest,
  CreateAddOrModifyCustomerContactRequest,
  CreateDeleteCustomerContactRequest,
} from "@/api/customerContacts";
import type { CustomerContact } from "@/Domain/CustomerContact";
import ContactDialog from "./ContactDialog.vue";
import ConfirmationModal from "./ConfirmationModal.vue";
import type { Contact } from "@/Domain/Contact";
import { useMainStore } from "@/stores/useMainStore";
import type { Changeset } from "@/Domain/Changeset";
import "@progress/kendo-theme-bootstrap/dist/all.css";
import { useNotificationStore } from "@/stores/useNotificationStore";
import { NotificationTypeEnum } from "@/Domain/NotificationTypeEnum";
import { EnumStatementType } from "@/Domain/EnumStatementType";
import { EnumFacilityType } from "@/Domain/EnumFacilityType";
import type { ErrorResponse } from "@/api";

interface Props {
  id: number;
  name: string;
  isShared?: boolean;
  isReadOnly: boolean;
  isCopacker: boolean;
  type: EnumFacilityType;
}

const props = defineProps<Props>();

const main = useMainStore();
const notificationStore = useNotificationStore();
const facilityName = ref(props.name);
const currentContact = ref({} as CustomerContact);
const oldWebContactEmail = ref({} as string);
const newWebContactEmail = ref({} as string);

const fieldToUpdateValue = ref(
  {} as string | number | boolean | string[] | Contact | Date | Changeset | null
);
const idToUpdate = ref({} as number);
const fieldToUpdateName = ref({} as keyof CustomerContact);

const modalVisible = ref(false);
const confirmationModalVisible = ref(false);

const readOnlyUser = computed(() => props.isReadOnly);

const isMainOffice = computed(() => props.type == EnumFacilityType.Company);

const enumStatementDisplayNames = [
  { value: EnumStatementType.Electronic, name: "Email" },
  { value: EnumStatementType.Neither, name: "N/A" },
];
const enumStatementTypeEntries = Object.entries(EnumStatementType);
const deliveryMethods = enumStatementTypeEntries
  .slice(enumStatementTypeEntries.length / 2, enumStatementTypeEntries.length)
  .map(([key, value]) => ({
    label: enumStatementDisplayNames.find((s) => s.value == value)?.name ?? key,
    value: value as number,
  }));

let companyLogin = main.companyLogin;
const refreshByFacilityContactsCounter = main.refreshByFacilityContactsCounter;

const columns: GridColumnProps[] = [
  {
    cell: "EditRow",
    width: "40px",
  },
  {
    title: "# of Facilities",
    field: "NumberOfFacilities",
    cell: "NumberOfFacilities",
    width: "70px",
  },
  {
    title: "Job Title",
    field: "companyTitle",
  },
  {
    title: "Title",
    field: "Contact.Title",
    width: "52px",
  },
  {
    title: "First Name",
    field: "Contact.Firstname",
  },
  {
    title: "Last Name",
    field: "Contact.Lastname",
  },
  {
    title: "Main",
    field: "primaryCT",
    cell: "radioPrimaryCT",
    width: "70px",
  },
  {
    title: "Billing",
    field: "billingCT",
    cell: "radioBillingCT",
    width: "75px",
  },
  {
    title: "Copacker",
    field: "copackerCT",
    cell: "radioCopackerCT",
  },
  {
    title: "OUDirect Admin",
    field: "webCT",
    cell: "radioWebCT",
  },
  {
    title: "Email",
    field: "Contact.Email",
    cell: "emailCell",
    width: "250px",
  },
  {
    title: "Phone",
    field: "Contact.Voice",
    width: "150px",
  },
  {
    title: "Invoice Delivery",
    field: "InvoiceDeliveryMethod",
    cell: "deliveryMethod",
    width: "85px",
  },
  {
    title: "Statement Delivery",
    field: "StatementDeliveryMethod",
    cell: "deliveryMethod",
    width: "85px",
  },
  {
    title: "Inspection Report Emails",
    field: "EIREmail",
    cell: "checkboxEir",
  },
  {
    title: "Ingredient Update Emails",
    field: "ScheduleAEmail",
    cell: "checkboxScheduleA",
  },
  {
    title: "Product Update Emails",
    field: "ScheduleBEmail",
    cell: "checkboxScheduleB",
  },
  {
    cell: "DeleteRow",
    width: "35px",
  },
];

function handleCheckboxInputChange(event: Event): boolean {
  return (event.target as HTMLInputElement).checked;
}

function showModal(isVisible: boolean, cc: CustomerContact | undefined) {
  if (cc) currentContact.value = cc;

  modalVisible.value = isVisible;
}
function handleDropdownListInputChange(event: Event): string {
  return (event.target as HTMLInputElement).value;
}

function showConfirmationModal(
  isVisible: boolean,
  _oldWebContactEmail: string,
  _newWebContactEmail: string
) {
  oldWebContactEmail.value = _oldWebContactEmail;
  newWebContactEmail.value = _newWebContactEmail;
  confirmationModalVisible.value = isVisible;
}

const request = ref<GetCustomerContactsRequest>({
  userLogin: main.userLogin,
  companyLogin: main.companyLogin,
  sortFields: "Contact.Lastname",
  sortDirs: "asc",
  isCompanyContact: isMainOffice.value,
  id: "" + props.id,
  allContacts: false,
  distinct: true,
  fields: "",
  operators: "",
  values: "",
  skip: 0,
  take: 10000,
});

const { error, data, mutate } = useSWRV(
  () => {
    let ret = ""; // build the key based on all the name.value pairs of the request object
    Object.keys(request.value).forEach(
      (x) => (ret += ":" + x + "=" + Object(request.value)[x])
    );
    console.debug("facility-contact-grid: useSWRV(): swrv key = " + ret);
    return ret;
  },
  () => {
    return api.get("GetCustomerContacts", request.value);
  }
);

columns[columns.findIndex((x) => x.field == "webCT")].hidden =
  !isMainOffice.value;
columns[columns.findIndex((x) => x.field == "copackerCT")].hidden =
  !props.isCopacker || !isMainOffice.value;
columns[columns.findIndex((x) => x.field == "StatementDeliveryMethod")].hidden =
  !isMainOffice.value;
columns[columns.findIndex((x) => x.cell == "EditRow")].hidden = false; //readOnlyUser
columns[columns.findIndex((x) => x.cell == "DeleteRow")].hidden =
  readOnlyUser.value;

if (props.isShared) {
  columns[columns.findIndex((x) => x.field == "EIREmail")].hidden = true;
}

async function reallyUpdateField<T extends keyof CustomerContact>() {
  console.debug("facility-contact-grid. reallyUpdateField():");

  if (!data || !data.value || !data.value.success) return;

  const customerContactToUpdate = data.value.response.ListItems.find(
    (x) => x.id == idToUpdate.value
  );
  if (!customerContactToUpdate) return;

  let previousCustomerContact = null;
  if (
    fieldToUpdateName.value == "webCT" ||
    fieldToUpdateName.value == "primaryCT" ||
    fieldToUpdateName.value == "billingCT" ||
    fieldToUpdateName.value == "copackerCT"
  )
    previousCustomerContact = data.value.response.ListItems.find(
      (x) => x[fieldToUpdateName.value]
    ); // row with this field radio button set to true

  if (
    fieldToUpdateName.value == "billingCT" ||
    fieldToUpdateName.value == "primaryCT"
  ) {
    customerContactToUpdate["InvoiceDeliveryMethod"] = customerContactToUpdate[
      "Contact"
    ].Email
      ? 1
      : 0;
    customerContactToUpdate["StatementDeliveryMethod"] =
      customerContactToUpdate["Contact"].Email ? 1 : 0;
  }

  // Not sure why TS can't infer the type of `field` here, so we manually cast it
  customerContactToUpdate[
    fieldToUpdateName.value as
      | "webCT"
      | "primaryCT"
      | "billingCT"
      | "copackerCT"
  ] = fieldToUpdateValue.value as boolean;

  const result = await api.post(
    "AddOrModifyCustomerContact",
    CreateAddOrModifyCustomerContactRequest(
      main.userLogin,
      main.companyLogin,
      false,
      customerContactToUpdate.FacilityId,
      props.name,
      customerContactToUpdate.Contact.Id,
      customerContactToUpdate.companyTitle,
      customerContactToUpdate
    )
  );

  if (result.success && result.response != "") {
    // For AddOrModifyCustomerContact a blank response indicates failure
    notificationStore.notify({
      message: `Contact ${customerContactToUpdate.Contact.Firstname} ${customerContactToUpdate.Contact.Lastname} was updated successfully`,
      type: NotificationTypeEnum.success,
      duration: 5000,
    });

    if (
      (fieldToUpdateName.value == "webCT" ||
        fieldToUpdateName.value == "primaryCT" ||
        fieldToUpdateName.value == "billingCT" ||
        fieldToUpdateName.value == "copackerCT") &&
      previousCustomerContact != null
    )
      previousCustomerContact[
        fieldToUpdateName.value as
          | "webCT"
          | "primaryCT"
          | "billingCT"
          | "copackerCT"
      ] = false; // Not sure why TS can't infer the type of `field` here, so we manually cast it

    await mutate();
  } else {
    console.debug("facility-contact-grid: reallyUpdateField(): failed.");
  }

  // If the field changed was not web contact, this modal was already closed so this line does nothing.
  // If it was, this line closes the modal, but after the mutate so the button is correctly selected.
  confirmationModalVisible.value = false;
}

async function updateField<T extends keyof CustomerContact>(
  id: number,
  field: T,
  value: CustomerContact[T]
) {
  console.debug(
    "facility-contact-grid. UpdateField(): id = " +
      id +
      ", field = " +
      field +
      ", value = " +
      value
  );

  idToUpdate.value = id;
  fieldToUpdateName.value = field;
  fieldToUpdateValue.value = value;

  if (field == "webCT" && data && data.value && data.value.success) {
    const customerContactToUpdate = data.value.response.ListItems.find(
      (x) => x.id == idToUpdate.value
    );
    const previousCustomerContact = data.value.response.ListItems.find(
      (x) => x[fieldToUpdateName.value]
    ); // row with this field radio button set to true
    if (customerContactToUpdate && previousCustomerContact) {
      showConfirmationModal(
        true,
        previousCustomerContact["Contact"].Email,
        customerContactToUpdate["Contact"].Email
      );
    }
  } else await reallyUpdateField();
}

async function handleConfirm() {
  // Keep the modal up until the changes have been made at the back end.
  // After the changes to the web contact button have been made the modal is taken down.
  await reallyUpdateField();
}

async function removeRow(id: number) {
  console.debug("facility-contact-grid: removeRow(): id = " + id);

  if (!data || !data.value || !data.value.success) return;

  const result = await api.get(
    "DeleteCustomerContact",
    CreateDeleteCustomerContactRequest(
      main.userLogin,
      main.companyLogin,
      props.name,
      id
    )
  );

  if (result.success && result.response != "") {
    console.debug(
      "facility-contact-grid: removeRow(): response = " + result.response
    );
    if (result.response.substring(0, 4) == "true") await mutate(); // refresh the grid so the deleted row doesn't show any more
  } else {
    console.debug("facility-contact-grid: removeRow(): failed.");
  }
}

function closeModal() {
  modalVisible.value = false;
}

async function closeConfirmationModal() {
  // nothing has changed, but the radio buttons need to be refreshed because they are incorrect.
  console.debug(
    "FacilityContactGrid.vue, closeConfirmationModal(): fieldToUpdateName.value = " +
      fieldToUpdateName.value +
      ", idToUpdate.value = " +
      idToUpdate.value +
      ", fieldToUpdateValue.value = " +
      fieldToUpdateValue.value
  );
  if (!data || !data.value || !data.value.success) return;

  // the following lines cause the grid to refresh and display the buttons properly.
  const customerContactToUpdate = data.value.response.ListItems.find(
    (x) => x.id == idToUpdate.value
  );
  const previousCustomerContact = data.value.response.ListItems.find(
    (x) => x[fieldToUpdateName.value]
  ); // row with this field radio button set to true
  if (customerContactToUpdate && previousCustomerContact) {
    previousCustomerContact[
      fieldToUpdateName.value as
        | "webCT"
        | "primaryCT"
        | "billingCT"
        | "copackerCT"
    ] = false as boolean; // Not sure why TS can't infer the type of `field` here, so we manually cast it
    customerContactToUpdate[
      fieldToUpdateName.value as
        | "webCT"
        | "primaryCT"
        | "billingCT"
        | "copackerCT"
    ] = true as boolean; // Not sure why TS can't infer the type of `field` here, so we manually cast it
  }
  main.refreshByFacilityContactsCounter++; // this will cause the other facility-contact-grids to refresh
  await mutate();

  confirmationModalVisible.value = false;
}

function modifyContact(c: Contact | undefined) {
  showModal(false, undefined);

  if (c) {
    mutate();
    // this will cause the other facility-contact-grids to refresh
    // in case the contacts was added to/deleted from any of them.
    main.refreshByFacilityContactsCounter++;
  }
}

watch(main, () => {
  // "main" can be changed in one of 3 ways.
  // 1. A new company is being emulated.
  // 2. The company hasn't changed, but a contact has been added or modified.
  console.debug(
    "FacilityContactGrid.vue, watch(): main changed. main.companyLogin = " +
      main.companyLogin +
      ", coompanyLogin = " +
      companyLogin
  );
  if (main.companyLogin != companyLogin) {
    console.debug(
      "FacilityContactGrid.vue, watch():company being emulated has changed. Setting companyLogin = " +
        main.companyLogin
    );
    companyLogin = main.companyLogin;
    request.value.companyLogin = main.companyLogin;
    mutate();
  } else if (
    main.refreshByFacilityContactsCounter != refreshByFacilityContactsCounter
  ) {
    console.debug(
      "FacilityContactGrid.vue, watch(): refreshByFacilityContactsCounter has changed"
    );
    mutate();
  }
});
</script>

<style scoped>
.del-icon {
  font-size: 16px;
  color: red;
  cursor: pointer;
}
.edit-icon {
  font-size: 18px;
  color: #222;
  cursor: pointer;
}

td,
:deep(td),
:deep(th) {
  font-size: 13px;
}
.tab-body__grid-title {
  background: white;
  padding: 15px;
  border-left: 1px solid #ddd;
  border-right: 1px solid #ddd;
  font-weight: 600;
  color: #333;
  border-bottom: 1px solid #ccc;
}
.tab-body__name {
  padding: 15px 15px;
  font-weight: 600;
  color: #333;
  border-right: 1px solid #ccc;
  border-left: 1px solid #ccc;
  border-bottom: 1px solid #ccc;
  background: #f7f7f7;
}
.loading {
  padding: 20px 10px;
  background: white;
  border: 1px solid #ddd;
}
.fa-edit {
  color: #999;
}
:deep(.k-column-title) {
  white-space: pre-wrap;
}
</style>
